import React, { useState, useEffect } from "react";
import Content from "../components/Content";
import Layout from "../containers/LayoutAdministrativo";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PacientesService from "../services/PacientesService";
import {
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Box,
  IconButton,
  Checkbox,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";
import { mostrarToast } from "../utils/toastUtils";
import { tieneMasDe10Digitos } from "../utils/telefonoUtils";

const calcularEdadDesdeFechaNacimiento = (fechaNacStr) => {
  if (!fechaNacStr) return "";
  const dateStr = String(fechaNacStr).trim().split("T")[0];
  if (!dateStr) return "";

  let birthDate;
  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      if (y && m && d) birthDate = new Date(y, m - 1, d);
    }
  } else if (dateStr.includes("/")) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        birthDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      } else {
        birthDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
      }
    }
  }

  if (!birthDate || isNaN(birthDate.getTime())) return "";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  birthDate.setHours(0, 0, 0, 0);

  if (birthDate > today) return "";

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  if (years >= 1) {
    return `${years} AÑOS`;
  } else if (months >= 1) {
    return `${months} MESES`;
  } else if (days >= 0) {
    return `${days} DIAS`;
  }
  return "";
};

const normalizarEdad = (val) => {
  if (!val) return "";
  const str = String(val).trim();
  if (str === "") return "";
  if (/^\d+$/.test(str)) {
    return `${str} AÑOS`;
  }
  return str.toUpperCase().replace(/DÍAS/g, "DIAS");
};

const Pacientes = () => {

  useEffect(() => {
    document.title = "Pacientes";
  }, []);

  function mostrarMensaje(mensaje) {
    mostrarToast(mensaje, "success");
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
  function mostrarError(mensaje) {
    Swal.fire({
      title: "¡Error!",
      text: mensaje,
      icon: "error",
      confirmButtonText: "Aceptar",
    });
  }

  async function mostrarBuscarPaciente() {
    let pacientesOriginal = [];
    let pacientesActivos = [];

    const ordenarPacientesRecientes = (lista) => {
      if (!Array.isArray(lista)) return [];
      return [...lista].sort(
        (a, b) => Number(b.idPaciente || b.id || 0) - Number(a.idPaciente || a.id || 0),
      );
    };

    const resultado = await PacientesService.getPacientes();
    pacientesOriginal = ordenarPacientesRecientes(resultado?.data?.pacientes || resultado?.pacientes || []);
    pacientesActivos = [...pacientesOriginal];

    const porPagina = 8;
    let paginaActual = 1;

    const construirFilas = (lista) => {
      return lista
        .map(
          (pac, idx) => `
          <tr style="background-color: ${idx % 2 === 0 ? "#ffffff" : "#f8fafc"}; border-bottom: 1px solid #e2e8f0; font-size: 0.80rem; transition: background 0.15s;">
            <td style="padding: 6px 12px; text-align: left; font-weight: 500; color: #1e293b; border-right: 1px solid #f1f5f9;">${pac.nombre} ${pac.apPaterno} ${pac.apMaterno}</td>
            <td style="padding: 4px 6px; text-align: center; border-right: 1px solid #f1f5f9;">
              <button data-action="mostrar" data-idpaciente="${pac.idPaciente}" title="Mostrar" style="background: #e0f2fe; color: #0284c7; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 0.85rem;">
                <i class="fas fa-eye"></i>
              </button>
            </td>
            <td style="padding: 4px 6px; text-align: center; border-right: 1px solid #f1f5f9;">
              <button data-action="editar" data-idpaciente="${pac.idPaciente}" title="Editar" style="background: #fef3c7; color: #d97706; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 0.85rem;">
                <i class="fas fa-edit"></i>
              </button>
            </td>
            <td style="padding: 4px 6px; text-align: center;">
              <button data-action="eliminar" data-idpaciente="${pac.idPaciente}" title="Eliminar" style="background: #fee2e2; color: #dc2626; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 0.85rem;">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        `,
        )
        .join("");
    };

    const renderPagina = (pagina) => {
      const inicio = (pagina - 1) * porPagina;
      const fin = inicio + porPagina;
      const paginaPacientes = pacientesActivos.slice(inicio, fin);
      const tabla = document.getElementById("tablaPacientes");
      if (tabla) {
        if (!paginaPacientes || paginaPacientes.length === 0) {
          tabla.innerHTML = `<tr><td colspan="4" style="padding: 24px; text-align: center; color: #64748b; font-style: italic;">No hay registros</td></tr>`;
        } else {
          tabla.innerHTML = construirFilas(paginaPacientes);
        }
        tabla
          .querySelectorAll("button[data-action='mostrar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const id = btn.dataset.idpaciente;
              const paciente = pacientesOriginal.find(
                (item) => String(item.idPaciente) === String(id),
              );
              if (paciente) {
                verPaciente(paciente);
                Swal.close();
              }
            });
          });
        tabla
          .querySelectorAll("button[data-action='editar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const id = btn.dataset.idpaciente;
              const paciente = pacientesOriginal.find(
                (item) => String(item.idPaciente) === String(id),
              );
              if (paciente) {
                editarPaciente(paciente);
                Swal.close();
              }
            });
          });
        tabla
          .querySelectorAll("button[data-action='eliminar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const id = btn.dataset.idpaciente;
              const paciente = pacientesOriginal.find(
                (item) => String(item.idPaciente) === String(id),
              );
              if (paciente) {
                Swal.fire({
                  title: "¿Eliminar paciente?",
                  text: `¿Desea eliminar al paciente ${paciente.nombre} ${paciente.apPaterno}?`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Sí, eliminar",
                  cancelButtonText: "Cancelar",
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    await eliminarPaciente(paciente.idPaciente);
                  }
                });
              }
            });
          });
      }

      const totalPaginas = Math.max(
        1,
        Math.ceil(pacientesActivos.length / porPagina),
      );
      const spanPagina = document.getElementById("paginaActual");
      if (spanPagina) {
        spanPagina.textContent = `Página ${pagina} de ${totalPaginas} (${pacientesActivos.length} registros)`;
      }
      const btnAnterior = document.getElementById("btnAnterior");
      const btnSiguiente = document.getElementById("btnSiguiente");
      if (btnAnterior) {
        btnAnterior.disabled = pagina <= 1;
        btnAnterior.style.opacity = pagina <= 1 ? "0.45" : "1";
        btnAnterior.style.cursor = pagina <= 1 ? "not-allowed" : "pointer";
      }
      if (btnSiguiente) {
        btnSiguiente.disabled = pagina >= totalPaginas;
        btnSiguiente.style.opacity = pagina >= totalPaginas ? "0.45" : "1";
        btnSiguiente.style.cursor = pagina >= totalPaginas ? "not-allowed" : "pointer";
      }
    };
    Swal.fire({
      title: "BUSCAR PACIENTE",
      width: "60%",
      customClass: {
        title: "tituloDegradado",
        popup: "swalMaxHeight",
      },
      html: `
          <div style="font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; text-align: left;">
            <div style="display: flex; gap: 10px; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; margin-bottom: 8px;">
              <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 220px;">
                <label style="font-size: 0.80rem; font-weight: 600; color: #334155;">Nombre del paciente:</label>
                <input id="buscarPaciente" type="text" style="padding: 5px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.82rem; outline: none; height: 32px; box-sizing: border-box;" placeholder="Escribe aquí...">
              </div>
              <div>
                <button id="btnMostrarTodos" style="background: linear-gradient(180deg, #0f4b75 0%, #006787 100%); color: white; border: none; border-radius: 6px; font-weight: 600; font-size: 0.80rem; padding: 6px 14px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); height: 32px; white-space: nowrap; transition: opacity 0.2s;">
                  Mostrar todos los pacientes
                </button>
              </div>
            </div>

            <div style="width: 100%; overflow: auto; max-height: 52vh; border: 1px solid #cbd5e1; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.08); background: white;">
              <table class="tablaPacientes" style="width: 100%; border-collapse: collapse; text-align: center;">
                <thead>
                  <tr style="background: linear-gradient(180deg, #0f4b75 0%, #006787 100%); color: white; font-size: 0.80rem; position: sticky; top: 0; z-index: 2;">
                    <th style="padding: 7px 12px; text-align: left; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600;">Nombre del paciente</th>
                    <th style="padding: 7px 8px; text-align: center; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600; width: 45px;">Mostrar</th>
                    <th style="padding: 7px 8px; text-align: center; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600; width: 45px;">Editar</th>
                    <th style="padding: 7px 8px; text-align: center; font-weight: 600; width: 45px;">Eliminar</th>
                  </tr>
                </thead>
                <tbody id="tablaPacientes">
                </tbody>
              </table>
            </div>

            <div style="margin-top: 8px; display: flex; justify-content: space-between; align-items: center;">
              <span id="paginaActual" style="font-size: 0.80rem; color: #64748b; font-weight: 500;"></span>
              <div style="display: flex; gap: 6px;">
                <button id="btnAnterior" style="padding: 4px 12px; background: #006787; color: white; border: none; border-radius: 6px; font-size: 0.80rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s;">Anterior</button>
                <button id="btnSiguiente" style="padding: 4px 12px; background: #006787; color: white; border: none; border-radius: 6px; font-size: 0.80rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s;">Siguiente</button>
              </div>
            </div>
          </div>
        `,
      showConfirmButton: false,
      didOpen: () => {
        renderPagina(paginaActual);

        document.getElementById("btnAnterior").addEventListener("click", () => {
          if (paginaActual > 1) {
            paginaActual--;
            renderPagina(paginaActual);
          }
        });

        document
          .getElementById("btnSiguiente")
          .addEventListener("click", () => {
            if (paginaActual < Math.ceil(pacientesActivos.length / porPagina)) {
              paginaActual++;
              renderPagina(paginaActual);
            }
          });

        document
          .getElementById("btnMostrarTodos")
          .addEventListener("click", async () => {
            try {
              const resultado = await PacientesService.getPacientes();
              pacientesOriginal = ordenarPacientesRecientes(resultado?.data?.pacientes || resultado?.pacientes || []);
              pacientesActivos = [...pacientesOriginal];
              paginaActual = 1;
              renderPagina(paginaActual);
            } catch (error) {
              console.error("Error al recargar pacientes:", error);
            }
            document.getElementById("buscarPaciente").value = "";
          });
        document
          .getElementById("buscarPaciente")
          .addEventListener("input", async (e) => {
            const valor = e.target.value.trim();
            paginaActual = 1;
            if (valor === "") {
              pacientesActivos = [...pacientesOriginal];
              renderPagina(paginaActual);
              return;
            }

            try {

              const resultado = await PacientesService.buscarPacientes(valor);
              pacientesActivos = ordenarPacientesRecientes(resultado?.pacientes || resultado?.data?.pacientes || []);
              renderPagina(paginaActual);
            } catch (error) {
              console.error("Error en búsqueda:", error);
            }
          });
      },
    });
  }

  const verPaciente = (paciente) => {
    setNombre(paciente.nombre || "");
    setApPaterno(paciente.apPaterno || "");
    setApMaterno(paciente.apMaterno || "");
    setSexo(paciente.sexo || "");
    setFechaNac(paciente.fechaNacimiento || "");
    setEdad(paciente.edad || "");
    setEmail(paciente.email || "");
    setIne(paciente.ine || "");
    setDireccion(paciente.direccion || "");
    setColonia(paciente.colonia || "");
    setCPostal(paciente.cPostal || "");
    setEstado(paciente.estado || "seleccione");
    setCiudad(paciente.ciudad || "");
    setTelefonoCasa(paciente.telefonoCasa || "");
    setTelefonoTrabajo(paciente.telefonoTrabajo || "");
    setTelefonoCel(paciente.telefonoCelular || "");
    setRfc(paciente.rfc || "");
    setRazonSocial(paciente.razonSocial || "");
    setDireccionFiscal(paciente.direccionFiscal || "");
    setColoniaFiscal(paciente.coloniaFiscal || "");
    setCPostalFiscal(paciente.cPostalFiscal || "");
    setEstadoFiscal(paciente.estadoFiscal || "seleccione");
    setCiudadFiscal(paciente.ciudadFiscal || "");
    setSelectedPacienteId(null);
    setFacturacionId(null);
    setEditMode(false);
    setViewOnly(true);
  };

  const editarPaciente = (paciente) => {
    setNombre(paciente.nombre || "");
    setApPaterno(paciente.apPaterno || "");
    setApMaterno(paciente.apMaterno || "");
    setSexo(paciente.sexo || "");
    setFechaNac(paciente.fechaNacimiento || "");
    setEdad(paciente.edad || "");
    setEmail(paciente.email || "");
    setIne(paciente.ine || "");
    setDireccion(paciente.direccion || "");
    setColonia(paciente.colonia || "");
    setCPostal(paciente.cPostal || "");
    setEstado(paciente.estado || "seleccione");
    setCiudad(paciente.ciudad || "");
    setTelefonoCasa(paciente.telefonoCasa || "");
    setTelefonoTrabajo(paciente.telefonoTrabajo || "");
    setTelefonoCel(paciente.telefonoCelular || "");
    setRfc(paciente.rfc || "");
    setRazonSocial(paciente.razonSocial || "");
    setDireccionFiscal(paciente.direccionFiscal || "");
    setColoniaFiscal(paciente.coloniaFiscal || "");
    setCPostalFiscal(paciente.cPostalFiscal || "");
    setEstadoFiscal(paciente.estadoFiscal || "seleccione");
    setCiudadFiscal(paciente.ciudadFiscal || "");
    setSelectedPacienteId(paciente.idPaciente);
    setFacturacionId(paciente.idFacturacion);
    setEditMode(true);
    setViewOnly(false);
  };

  function reloadPage() {
    window.location.reload();
  }
  const [nombre, setNombre] = useState("");
  const [apPaterno, setApPaterno] = useState("");
  const [apMaterno, setApMaterno] = useState("");
  const [sexo, setSexo] = useState("");
  const [fechaNac, setFechaNac] = useState("");
  const [email, setEmail] = useState("");
  const [ine, setIne] = useState("");
  const [direccion, setDireccion] = useState("");
  const [colonia, setColonia] = useState("");
  const [cPostal, setCPostal] = useState("");
  const [estado, setEstado] = useState("seleccione");
  const [ciudad, setCiudad] = useState("");
  const [telefonoCasa, setTelefonoCasa] = useState("");
  const [telefonoTrabajo, setTelefonoTrabajo] = useState("");
  const [telefonoCel, setTelefonoCel] = useState("");
  const [rfc, setRfc] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [direccionFiscal, setDireccionFiscal] = useState("");
  const [coloniaFiscal, setColoniaFiscal] = useState("");
  const [cPostalFiscal, setCPostalFiscal] = useState("");
  const [estadoFiscal, setEstadoFiscal] = useState("seleccione");
  const [ciudadFiscal, setCiudadFiscal] = useState("");
  const [errorNombre, setErrorNombre] = useState(false);
  const [errorApPaterno, setErrorApPaterno] = useState(false);
  const [errorSexo, setErrorSexo] = useState(false);
  const [edad, setEdad] = useState("");
  const [selectedPacienteId, setSelectedPacienteId] = useState(null);
  const [facturacionId, setFacturacionId] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const registrarPac = async () => {
    if (nombre === "" && apPaterno === "" && sexo === "") {
      mostrarError("Rellene los cambios obligatorios!");
      setErrorNombre(true);
      setErrorApPaterno(true);
      setErrorSexo(true);
    } else if (nombre === "") {
      mostrarError("Rellene los cambios obligatorios!");
      setErrorNombre(true);
    } else if (apPaterno === "") {
      mostrarError("Rellene los cambios obligatorios!");
      setErrorApPaterno(true);
    } else if (sexo === "") {
      mostrarError("Rellene los cambios obligatorios!");
      setErrorSexo(true);
    } else if (tieneMasDe10Digitos(telefonoCasa)) {
      mostrarError("El teléfono de casa no puede tener más de 10 dígitos.");
    } else if (tieneMasDe10Digitos(telefonoTrabajo)) {
      mostrarError("El teléfono de trabajo no puede tener más de 10 dígitos.");
    } else if (tieneMasDe10Digitos(telefonoCel)) {
      mostrarError("El teléfono celular no puede tener más de 10 dígitos.");
    } else {
      let paciente;
      let pacienteUpd;
      let compEstado;
      const edadNormalizada = normalizarEdad(edad);
      if (checked) {
        if (estado === "seleccione") {
          compEstado = "";
        } else {
          compEstado = estado;
        }
        paciente = {
          nombre: nombre,
          apPaterno: apPaterno,
          apMaterno: apMaterno,
          sexo: sexo,
          email: email,
          fechaNacimiento: fechaNac,
          edad: edadNormalizada,
          ine: ine,
          direccion: direccion,
          colonia: colonia,
          cPostal: cPostal,
          estado: compEstado,
          ciudad: ciudad,
          telefonoCasa: telefonoCasa,
          telefonoTrabajo: telefonoTrabajo,
          telefonoCelular: telefonoCel,
          rfc: rfc,
          razonSocial: razonSocial,
          direccionFiscal: direccion,
          coloniaFiscal: colonia,
          cPostalFiscal: cPostal,
          estadoFiscal: compEstado,
          ciudadFiscal: ciudad,
        };

        pacienteUpd = {
          nombreN: nombre,
          apPaternoN: apPaterno,
          apMaternoN: apMaterno,
          sexoN: sexo,
          emailN: email,
          fechaNacimientoN: fechaNac,
          edadN: edadNormalizada,
          ineN: ine,
          direccionN: direccion,
          coloniaN: colonia,
          cPostalN: cPostal,
          estadoN: compEstado,
          ciudadN: ciudad,
          telefonoCasaN: telefonoCasa,
          telefonoTrabajoN: telefonoTrabajo,
          telefonoCelularN: telefonoCel,
          idFacturacionN: facturacionId,
          rfcN: rfc,
          razonSocialN: razonSocial,
          direccionFiscalN: direccion,
          coloniaFiscalN: colonia,
          cPostalFiscalN: cPostal,
          estadoFiscalN: compEstado,
          ciudadFiscalN: ciudad,
        };
      } else {
        let compEstadoFiscal;
        if (estado === "seleccione") {
          compEstado = "";
        } else {
          compEstado = estado;
        }
        if (estadoFiscal === "seleccione") {
          compEstadoFiscal = "";
        } else {
          compEstadoFiscal = estadoFiscal
        }
        paciente = {
          nombre: nombre,
          apPaterno: apPaterno,
          apMaterno: apMaterno,
          sexo: sexo,
          email: email,
          fechaNacimiento: fechaNac,
          edad: edadNormalizada,
          ine: ine,
          direccion: direccion,
          colonia: colonia,
          cPostal: cPostal,
          estado: compEstado,
          ciudad: ciudad,
          telefonoCasa: telefonoCasa,
          telefonoTrabajo: telefonoTrabajo,
          telefonoCelular: telefonoCel,
          rfc: rfc,
          razonSocial: razonSocial,
          direccionFiscal: direccionFiscal,
          coloniaFiscal: coloniaFiscal,
          cPostalFiscal: cPostalFiscal,
          estadoFiscal: compEstadoFiscal,
          ciudadFiscal: ciudadFiscal,
        };

        pacienteUpd = {
          nombreN: nombre,
          apPaternoN: apPaterno,
          apMaternoN: apMaterno,
          sexoN: sexo,
          emailN: email,
          fechaNacimientoN: fechaNac,
          edadN: edadNormalizada,
          ineN: ine,
          direccionN: direccion,
          coloniaN: colonia,
          cPostalN: cPostal,
          estadoN: compEstado,
          ciudadN: ciudad,
          telefonoCasaN: telefonoCasa,
          telefonoTrabajoN: telefonoTrabajo,
          telefonoCelularN: telefonoCel,
          idFacturacionN: facturacionId,
          rfcN: rfc,
          razonSocialN: razonSocial,
          direccionFiscalN: direccionFiscal,
          coloniaFiscalN: coloniaFiscal,
          cPostalFiscalN: cPostalFiscal,
          estadoFiscalN: compEstadoFiscal,
          ciudadFiscalN: ciudadFiscal,
        };
      }

      try {
        let resultado;
        if (editMode && selectedPacienteId) {
          resultado = await PacientesService.updatePaciente(
            pacienteUpd,
            selectedPacienteId,
          );
        } else {
          resultado = await PacientesService.agregarPaciente(paciente);
        }
        if (resultado.data.estatus === "OK") {
          mostrarMensaje(
            editMode
              ? "Paciente actualizado con éxito!"
              : "Paciente agregado con exito!",
          );
        } else {
          mostrarError(
            editMode
              ? "No se pudo actualizar el paciente: " + resultado.data.mensaje
              : "No se pudo agregar el paciente: " + resultado.data.mensaje,
          );
        }
      } catch (error) {
        mostrarError(
          editMode
            ? "Error al actualizar paciente:"
            : "Error al agregar paciente:",
          error,
        );
      }
    }
  };
  const eliminarPaciente = async (pacienteId) => {
    try {
      const resultado = await PacientesService.deletePaciente(pacienteId);
      if (resultado.data.estatus === "OK") {
        mostrarMensaje("Paciente eliminado con éxito!");
      } else {
        mostrarError("Error al eliminar paciente");
      }
    } catch (error) {
      console.error("Error al eliminar paciente:", error);
      mostrarError("Error al eliminar paciente");
    }
  };

  const [checked, setChecked] = useState(false);
  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "50%",
            paddingTop: "2vh",
            paddingBottom: "2vh",
          }}
        >
          <Stack spacing={2} sx={{ width: "95vw", maxWidth: "96vw" }}>
            <Paper
              elevation={3}
              sx={{ p: 2, borderRadius: 4 }}
              className="paperPac"
            >
              <Typography
                variant="h7"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: "linear-gradient(90deg, #006787, #014e65)",
                  color: "white",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  display: "inline-block",
                  fontWeight: "bold",
                }}
              >
                DATOS PRINCIPALES
              </Typography>

              {/* Contenedor principal */}
              <Grid container spacing={"0.3vw"}>
                {/* Nombre y apellidos */}
                <Grid
                  item
                  xs={12}
                  container
                  spacing={"1vw"}
                  sx={{ paddingTop: "0.4vw" }}
                >
                  <Stack direction="row" spacing={"3vw"}>
                    <Grid item xs={2}>
                      <InputLabel
                        htmlFor="txfNombre"
                        sx={{
                          color: "black",
                          fontSize: "1vw",
                          fontWeight: "bold",
                        }}
                      >
                        Nombre: <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={nombre}
                        disabled={viewOnly}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          if (errorNombre && e.target.value.trim() !== "") {
                            setErrorNombre(false);
                          }
                        }}
                        error={errorNombre}
                        helperText={errorNombre ? "Nombre obligatorio" : ""}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "5vh",
                            width: "13vw"
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "1vw",
                            padding: "0.5vw",
                          },
                        }}
                        className="txfNombre"
                        required
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <InputLabel
                        htmlFor="txfApPaterno"
                        sx={{
                          color: "black",
                          fontSize: "1vw",
                          fontWeight: "bold",
                        }}
                      >
                        Apellido Paterno:{" "}
                        <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={apPaterno}
                        disabled={viewOnly}
                        onChange={(e) => {
                          setApPaterno(e.target.value);
                          if (errorApPaterno && e.target.value.trim() !== "") {
                            setErrorApPaterno(false);
                          }
                        }}
                        error={errorApPaterno}
                        helperText={
                          errorApPaterno ? "Apellido paterno obligatorio" : ""
                        }
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "5vh",
                            width: "13vw"
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "1vw",
                            padding: "0.5vw",
                          },
                        }}
                        className="txfApPaterno"
                        required
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <InputLabel
                        htmlFor="txfApMaterno"
                        sx={{
                          color: "black",
                          fontSize: "1vw",
                          fontWeight: "bold",
                        }}
                      >
                        Apellido Materno:
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={apMaterno}
                        disabled={viewOnly}
                        onChange={(e) => {
                          setApMaterno(e.target.value);
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "5vh",
                            width: "13vw"
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "1vw",
                            padding: "0.5vw",
                          },
                        }}
                        className="txfApMaterno"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl>
                        <FormLabel
                          sx={{
                            color: "black",
                            fontSize: "1vw",
                            fontWeight: "bold",
                          }}
                        >
                          Sexo:<span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <RadioGroup
                          row
                          value={sexo}
                          error={errorSexo}
                          helperText={errorSexo ? " Parametro Sexo obligatorio" : ""}
                          onChange={(e) => {
                            setSexo(e.target.value);
                          }}
                          disabled={viewOnly}
                        >
                          <FormControlLabel
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "1vw",
                                fontWeight: "bold",
                              },
                            }}
                            value="M"
                            control={
                              <Radio
                                disabled={viewOnly}
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    height: "1vw",
                                    width: "1vw",
                                  },
                                }}
                              />
                            }
                            label="Masculino"
                          />
                          <FormControlLabel
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "1vw",
                                fontWeight: "bold",
                              },
                            }}
                            value="F"
                            control={
                              <Radio
                                disabled={viewOnly}
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    height: "1vw",
                                    width: "1vw",
                                  },
                                }}
                              />
                            }
                            label="Femenino"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Stack>
                  {/* Sexo y estado civil */}

                  <Grid
                    item
                    xs={12}
                    container
                    spacing={"1vw"}
                    sx={{ paddingTop: "0.4vw" }}
                  >
                    <Stack direction="row" spacing={"3vw"}>
                      <Grid item xs={2}>
                        <InputLabel
                          htmlFor="txfEmail"
                          sx={{
                            color: "black",
                            fontSize: "1vw",
                            fontWeight: "bold",
                          }}
                        >
                          Email:
                        </InputLabel>
                        <TextField
                          fullWidth
                          value={email}
                          disabled={viewOnly}
                          onChange={(e) => {
                            setEmail(e.target.value);
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "5vh",
                              width: "13vw"
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "1vw",
                              padding: "0.5vw",
                            },
                          }}
                          className="txfEmail"
                          type="email"
                        />
                      </Grid>

                      {/* Fecha de nacimiento */}
                      <Grid item xs={2}>
                        <InputLabel
                          htmlFor="txfFechaNacimiento"
                          sx={{
                            color: "black",
                            fontSize: "1vw",
                            fontWeight: "bold",
                          }}
                        >
                          Fecha de Nacimiento:
                        </InputLabel>
                        <TextField
                          fullWidth
                          value={fechaNac}
                          disabled={viewOnly}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFechaNac(val);
                            const edadCalc = calcularEdadDesdeFechaNacimiento(val);
                            if (edadCalc) {
                              setEdad(edadCalc);
                            }
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              width: "13vw",
                              height: "5vh",
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "1vw",
                              padding: "0.5vw",
                            },
                          }}
                          className="txfFechaNacimiento"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {/* Email y teléfonos */}
                      <Grid item xs={6}>
                        <InputLabel
                          htmlFor="txfEdad"
                          sx={{
                            color: "black",
                            fontSize: "1vw",
                            fontWeight: "bold",
                          }}
                        >
                          Edad:
                        </InputLabel>
                        <TextField
                          fullWidth
                          value={edad}
                          disabled={viewOnly}
                          onChange={(e) => {
                            setEdad(e.target.value);
                          }}
                          onBlur={(e) => {
                            setEdad(normalizarEdad(e.target.value));
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "5vh",
                              width: "13vw"
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "1vw",
                              padding: "0.5vw",
                            },
                          }}
                          className="txfEdad"

                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InputLabel
                          htmlFor="txfINE"
                          sx={{
                            color: "black",
                            fontSize: "1vw",
                            fontWeight: "bold",
                          }}
                        >
                          INE/DNI:
                        </InputLabel>
                        <TextField
                          fullWidth
                          value={ine}
                          disabled={viewOnly}
                          onChange={(e) => {
                            setIne(e.target.value);
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "5vh",
                              width: "13vw"
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "1vw",
                              padding: "0.5vw",
                            },
                          }}
                          className="txfINE"
                        />
                      </Grid>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
            <Paper
              elevation={3}
              sx={{ p: 2, borderRadius: 4 }}
              className="paperPacCont"
            >
              {/* Lugar de atencion */}
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0vw" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h7"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      background: "linear-gradient(90deg, #006787, #014e65)",
                      color: "white",
                      padding: "0.5vw 1vw",
                      borderRadius: "8px",
                      display: "inline-block",
                      marginTop: "0vw",
                      fontWeight: "bold"
                    }}
                  >
                    DATOS DE CONTACTO
                  </Typography>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0.4vw" }}
              >
                <Stack direction="row" spacing={"3vw"}>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfDireccion"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Dirección:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={direccion}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setDireccion(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfDireccion"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfColonia"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Colonia:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={colonia}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setColonia(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfColonia"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfCPostal"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Código Postal:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={cPostal}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setCPostal(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfPostal"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor="selectEstado"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Estado:
                    </InputLabel>
                    <Select
                      input={<OutlinedInput />}
                      value={estado}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setEstado(e.target.value);
                      }}
                      sx={{
                        flex: "0 0 8vw",
                        width: "13vw",
                        maxWidth: "100%",

                        height: "5vh",
                        minHeight: "0px !important",
                        overflow: "hidden",


                        "&.MuiInputBase-root": {
                          minHeight: "0px !important",
                        },

                        "& .MuiSelect-select": {
                          minHeight: "0px !important",
                          padding: "0 0.5vw !important",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "1vw",
                          lineHeight: "1",
                        },

                        "& .MuiOutlinedInput-notchedOutline": {

                          top: 0,
                          "& legend": { display: "none" },
                        },

                        "& .MuiSvgIcon-root": {
                          display: "inline-block !important",
                          position: "absolute",
                          right: "0.5vw",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "1.2vw !important",
                          width: "1.2vw !important",
                          height: "1.2vw !important",
                          pointerEvents: "none",

                        },
                        minWidth: "0px !important",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                      className="selectEstado"
                    >
                      <MenuItem
                        sx={{
                          fontSize: "1vw",
                          padding: "0.5vw",
                        }}
                        value="seleccione"
                        disabled
                      >
                        Seleccione
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Aguascalientes"
                      >
                        Aguascalientes
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Baja California"
                      >
                        Baja California
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Baja California Sur"
                      >
                        Baja California Sur
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Campeche"
                      >
                        Campeche
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Chiapas"
                      >
                        Chiapas
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Chihuahua"
                      >
                        Chihuahua
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Ciudad de México"
                      >
                        Ciudad de México
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Coahuila"
                      >
                        Coahuila
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Colima"
                      >
                        Colima
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Durango"
                      >
                        Durango
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Estado de México"
                      >
                        Estado de México
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Guanajuato"
                      >
                        Guanajuato
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Guerrero"
                      >
                        Guerrero
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Hidalgo"
                      >
                        Hidalgo
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Jalisco"
                      >
                        Jalisco
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Michoacán"
                      >
                        Michoacán
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Morelos"
                      >
                        Morelos
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Nayarit"
                      >
                        Nayarit
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Nuevo León"
                      >
                        Nuevo León
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Oaxaca"
                      >
                        Oaxaca
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Puebla"
                      >
                        Puebla
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Querétaro"
                      >
                        Querétaro
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Quintana Roo"
                      >
                        Quintana Roo
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="San Luis Potosí"
                      >
                        San Luis Potosí
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Sinaloa"
                      >
                        Sinaloa
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Sonora"
                      >
                        Sonora
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tabasco"
                      >
                        Tabasco
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tamaulipas"
                      >
                        Tamaulipas
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tlaxcala"
                      >
                        Tlaxcala
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Veracruz"
                      >
                        Veracruz
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Yucatán"
                      >
                        Yucatán
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Zacatecas"
                      >
                        Zacatecas
                      </MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor="txfCiudad"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Ciudad:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={ciudad}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setCiudad(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfCiudad"
                    />
                  </Grid>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0.8vw" }}
              >
                <Stack direction="row" spacing={"3vw"}>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfTelefonoCasa"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Teléfono(Casa):
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={telefonoCasa}
                      disabled={viewOnly}
                      error={tieneMasDe10Digitos(telefonoCasa)}
                      helperText={tieneMasDe10Digitos(telefonoCasa) ? "Máximo 10 dígitos" : ""}
                      onChange={(e) => {
                        setTelefonoCasa(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfTelefonoCasa"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfTelefonoTrabajo"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Teléfono(Trabajo):
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={telefonoTrabajo}
                      disabled={viewOnly}
                      error={tieneMasDe10Digitos(telefonoTrabajo)}
                      helperText={tieneMasDe10Digitos(telefonoTrabajo) ? "Máximo 10 dígitos" : ""}
                      onChange={(e) => {
                        setTelefonoTrabajo(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfTelefonoTrabajo"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfTelefonoCel"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Teléfono(Celular):
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={telefonoCel}
                      disabled={viewOnly}
                      error={tieneMasDe10Digitos(telefonoCel)}
                      helperText={tieneMasDe10Digitos(telefonoCel) ? "Máximo 10 dígitos" : ""}
                      onChange={(e) => {
                        setTelefonoCel(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfTelefonoCel"
                    />
                  </Grid>
                </Stack>
              </Grid>
            </Paper>
            <Paper
              elevation={3}
              sx={{ p: 2, borderRadius: 4 }}
              className="paperPacFac"
            >
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0vw" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h7"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      background: "linear-gradient(90deg, #006787, #014e65)",
                      color: "white",
                      padding: "0.5vw 1vw",
                      borderRadius: "8px",
                      display: "inline-block",
                      marginTop: "0vw",
                      fontWeight: "bold",
                    }}
                  >
                    DATOS DE FACTURACIÓN
                  </Typography>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                container
                spacing={"10vw"}
                sx={{ paddingTop: "0.4vw" }}
              >
                <Stack direction="row" spacing={"3vw"}>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfRFC"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      RFC:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={rfc}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setRfc(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfRFC"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfRazonSocial"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Razón Social:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={razonSocial}
                      disabled={viewOnly}
                      onChange={(e) => {
                        setRazonSocial(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfRazonSocial"
                    />
                  </Grid>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0.4vw" }}
              >
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setChecked(isChecked);
                          if (isChecked) {
                            setDireccionFiscal("");
                            setColoniaFiscal("");
                            setCPostalFiscal("");
                            setEstadoFiscal("seleccione");
                            setCiudadFiscal("");
                          }
                        }}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "1.5vw",
                          },
                        }}
                      />
                    }
                    label="La dirección de facturación es la misma que la de paciente"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "1vw",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                container
                spacing={"1vw"}
                sx={{ paddingTop: "0.4vw" }}
              >
                <Stack direction="row" spacing={"3vw"}>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfDireccion"
                      sx={{
                        color: checked || viewOnly ? "gray" : "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Dirección:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={direccionFiscal}
                      disabled={checked || viewOnly}
                      onChange={(e) => {
                        setDireccionFiscal(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfDireccion"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfColonia"
                      sx={{
                        color: checked || viewOnly ? "gray" : "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Colonia:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={coloniaFiscal}
                      disabled={checked || viewOnly}
                      onChange={(e) => {
                        setColoniaFiscal(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfColonia"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel
                      htmlFor="txfCPostal"
                      sx={{
                        color: checked || viewOnly ? "gray" : "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Código Postal:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={cPostalFiscal}
                      disabled={checked || viewOnly}
                      onChange={(e) => {
                        setCPostalFiscal(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfPostal"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor="selectEstado"
                      sx={{
                        color: checked || viewOnly ? "gray" : "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Estado:
                    </InputLabel>
                    <Select
                      input={<OutlinedInput />}
                      value={estadoFiscal}
                      disabled={checked || viewOnly}
                      onChange={(e) => {
                        setEstadoFiscal(e.target.value);
                      }}
                      sx={{
                        flex: "0 0 8vw",
                        width: "13vw",
                        maxWidth: "100%",

                        height: "5vh",
                        minHeight: "0px !important",
                        overflow: "hidden",


                        "&.MuiInputBase-root": {
                          minHeight: "0px !important",
                        },

                        "& .MuiSelect-select": {
                          minHeight: "0px !important",
                          padding: "0 0.5vw !important",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "1vw",
                          lineHeight: "1",
                        },

                        "& .MuiOutlinedInput-notchedOutline": {

                          top: 0,
                          "& legend": { display: "none" },
                        },

                        "& .MuiSvgIcon-root": {
                          display: "inline-block !important",
                          position: "absolute",
                          right: "0.5vw",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "1.2vw !important",
                          width: "1.2vw !important",
                          height: "1.2vw !important",
                          pointerEvents: "none",

                        },
                        minWidth: "0px !important",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                      className="selectEstado"
                    >
                      <MenuItem
                        sx={{
                          fontSize: "1vw",
                          padding: "0.5vw",
                        }}
                        value="seleccione"
                        disabled
                      >
                        Seleccione
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Aguascalientes"
                      >
                        Aguascalientes
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Baja California"
                      >
                        Baja California
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Baja California Sur"
                      >
                        Baja California Sur
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Campeche"
                      >
                        Campeche
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Chiapas"
                      >
                        Chiapas
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Chihuahua"
                      >
                        Chihuahua
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Ciudad de México"
                      >
                        Ciudad de México
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Coahuila"
                      >
                        Coahuila
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Colima"
                      >
                        Colima
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Durango"
                      >
                        Durango
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Estado de México"
                      >
                        Estado de México
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Guanajuato"
                      >
                        Guanajuato
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Guerrero"
                      >
                        Guerrero
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Hidalgo"
                      >
                        Hidalgo
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Jalisco"
                      >
                        Jalisco
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Michoacán"
                      >
                        Michoacán
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Morelos"
                      >
                        Morelos
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Nayarit"
                      >
                        Nayarit
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Nuevo León"
                      >
                        Nuevo León
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Oaxaca"
                      >
                        Oaxaca
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Puebla"
                      >
                        Puebla
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Querétaro"
                      >
                        Querétaro
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Quintana Roo"
                      >
                        Quintana Roo
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="San Luis Potosí"
                      >
                        San Luis Potosí
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Sinaloa"
                      >
                        Sinaloa
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Sonora"
                      >
                        Sonora
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tabasco"
                      >
                        Tabasco
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tamaulipas"
                      >
                        Tamaulipas
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Tlaxcala"
                      >
                        Tlaxcala
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Veracruz"
                      >
                        Veracruz
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Yucatán"
                      >
                        Yucatán
                      </MenuItem>
                      <MenuItem
                        sx={{ fontSize: "1vw", padding: "0.5vw" }}
                        value="Zacatecas"
                      >
                        Zacatecas
                      </MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={2}>
                    <InputLabel
                      htmlFor="txfCiudad"
                      sx={{
                        color: "black",
                        fontSize: "1vw",
                        fontWeight: "bold",
                      }}
                    >
                      Ciudad:
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={ciudadFiscal}
                      disabled={checked || viewOnly}
                      onChange={(e) => {
                        setCiudadFiscal(e.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "5vh",
                          width: "13vw"
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "1vw",
                          padding: "0.5vw",
                        },
                      }}
                      className="txfCiudad"
                    />
                  </Grid>
                </Stack>
              </Grid>
            </Paper>
          </Stack>
        </Box>
        <Box
          sx={{
            position: "fixed",
            right: "6vw",
            top: "30%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {!viewOnly && (
            <Tooltip
              title={"Guardar paciente"}
              arrow
              placement="right"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                    {
                      marginTop: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                    {
                      marginBottom: "0.2vw",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                    {
                      marginLeft: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                    {
                      marginRight: "0px",
                    },
                  },
                },
                tooltip: {
                  sx: {
                    fontSize: "0.7vw",
                    padding: "0.5vw 0.5vw",
                    borderRadius: "8px",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    fontWeight: "bold",
                    fontFamily: "Roboto-Medium",
                    color: "#000000",
                    marginBottom: "-5px",
                  },
                },
                arrow: {
                  sx: {
                    color: "rgba(255,255,255,0.9)",
                  },
                },
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#18234f",
                  "&:hover": { backgroundColor: "#d1d1d1" },
                  width: "4vw",
                  height: "4vw",
                }}
                onClick={registrarPac}
              >
                <SaveIcon sx={{ fontSize: "1.8vw" }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip
            title={"Nuevo paciente"}
            arrow
            placement="right"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: "0.2vw",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                  {
                    marginLeft: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                  {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.7vw",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontWeight: "bold",
                  fontFamily: "Roboto-Medium",
                  color: "#000000",
                  marginBottom: "-5px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                },
              },
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "#ffffff",
                color: "#18234f",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "4vw",
                height: "4vw",
              }}
              onClick={reloadPage}
            >
              <AddIcon sx={{ fontSize: "1.8vw" }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={"Buscar paciente"}
            arrow
            placement="right"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: "0.2vw",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                  {
                    marginLeft: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                  {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.7vw",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontWeight: "bold",
                  fontFamily: "Roboto-Medium",
                  color: "#000000",
                  marginBottom: "-5px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                },
              },
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "#ffffff",
                color: "#18234f",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "4vw",
                height: "4vw",
              }}
              onClick={mostrarBuscarPaciente}
            >
              <SearchIcon sx={{ fontSize: "1.8vw" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};
export default Pacientes;
